import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, UploadInput } from 'components/UIElements'
import { tmgZipFileIcon, tmgZipRemoveIcon, tmgZipReplaceIcon } from 'assets/assets'
import STRINGS from 'utils/strings'
import { TIMED_MATCHING_GRID_KEY_SLOT_COUNT } from 'utils/constants'
import { revokeSymbolPreviewUrls, validateSymbolKeyZip } from 'utils/timedMatchingGrid'
import { convertToMB } from 'utils/misc'

const formatFileSize = size => {
  if (!size && size !== 0) return ''
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${convertToMB(size)} MB`
}

/**
 * Dialog body for ZIP-only symbol key upload (GOLD-12192).
 * After a ZIP is selected, always shows the file row + key preview.
 * Valid ZIP fills symbol slots; invalid ZIP keeps hyphens and shows the error.
 * Save key uploads each extracted SVG/PNG one-by-one via POST /control/survey_image, then commits the key.
 */
const SymbolKeyUploadDialog = ({
  initialFile = null,
  onCancel,
  onSave,
  slotCount = TIMED_MATCHING_GRID_KEY_SLOT_COUNT,
  studyID,
  uploadImage,
}) => {
  const [zipFile, setZipFile] = useState(initialFile || null)
  const [symbols, setSymbols] = useState([])
  const [error, setError] = useState('')
  const [uploadError, setUploadError] = useState('')
  const [statusText, setStatusText] = useState(initialFile ? 'Validating…' : '')
  const [isValidating, setIsValidating] = useState(!!initialFile)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState('')
  const symbolsRef = useRef([])
  const replaceInputRef = useRef(null)
  const initialFileHandledRef = useRef(false)
  const savedRef = useRef(false)

  useEffect(() => {
    return () => {
      // After Save key, preview URLs are owned by the builder — do not revoke them
      if (!savedRef.current) {
        revokeSymbolPreviewUrls(symbolsRef.current)
      }
    }
  }, [])

  const clearPending = () => {
    revokeSymbolPreviewUrls(symbolsRef.current)
    symbolsRef.current = []
    setSymbols([])
    setZipFile(null)
    setStatusText('')
    setError('')
    setUploadError('')
  }

  const onUpdateFile = async fileNode => {
    if (!fileNode || !fileNode.name) {
      clearPending()
      return
    }
    setIsValidating(true)
    setError('')
    setUploadError('')
    revokeSymbolPreviewUrls(symbolsRef.current)
    symbolsRef.current = []
    setSymbols([])
    setZipFile(fileNode)
    setStatusText('Validating…')

    const result = await validateSymbolKeyZip(fileNode, slotCount)
    setIsValidating(false)
    if (!result.ok) {
      setError(result.error)
      setStatusText(`0 of ${slotCount} symbols found`)
      // Keep zipFile so Replace/Remove + empty key preview remain visible
      return
    }
    symbolsRef.current = result.symbols
    setSymbols(result.symbols)
    setStatusText(result.statusText)
    setError('')
  }

  // Process ZIP selected from the builder before the dialog opened
  useEffect(() => {
    if (!initialFile || initialFileHandledRef.current) return
    initialFileHandledRef.current = true
    onUpdateFile(initialFile)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFile])

  const onReplaceClick = () => {
    if (replaceInputRef.current) {
      replaceInputRef.current.value = ''
      replaceInputRef.current.click()
    }
  }

  const onReplaceInputChange = e => {
    const nextFile = e.target.files?.[0]
    if (nextFile) onUpdateFile(nextFile)
  }

  const onRemove = () => {
    clearPending()
  }

  const keyComplete = symbols.length === slotCount && !error
  const fileMetaLine = [formatFileSize(zipFile?.size), statusText].filter(Boolean).join(' · ')
  const symbolsByDigit = symbols.reduce((acc, symbol) => {
    acc[symbol.digit] = symbol
    return acc
  }, {})

  const handleSave = async () => {
    if (!keyComplete || isUploading) return
    if (!uploadImage || !studyID) {
      setUploadError(STRINGS.tmgUploadSymbolsFailed)
      return
    }

    setIsUploading(true)
    setUploadError('')
    const committed = []

    try {
      /* Upload extracted files one-by-one so a failed symbol does not leave a partial key. */
      /* eslint-disable no-await-in-loop */
      for (let i = 0; i < symbols.length; i += 1) {
        const symbol = symbols[i]
        setUploadProgress(`${STRINGS.tmgUploadingSymbols} ${i + 1} of ${symbols.length}`)
        const formData = new FormData()
        formData.set('study_id', `${studyID}`)
        formData.set('image_name', symbol.fileName || symbol.file?.name || `${symbol.digit}.svg`)
        formData.set('survey_image', symbol.file)
        formData.set('alt_text', `symbol ${symbol.digit}`)
        const payload = await uploadImage(formData, { silent: true })
        if (!payload?.imageId) {
          throw new Error('upload failed')
        }
        committed.push({
          digit: symbol.digit,
          image: payload.imageId,
          imageSrc: symbol.previewUrl || payload.src || payload.imageId,
          size: symbol.file?.size,
          fileName: symbol.fileName,
        })
      }
      /* eslint-enable no-await-in-loop */
      savedRef.current = true
      symbolsRef.current = []
      onSave(committed)
    } catch (e) {
      setUploadError(STRINGS.tmgUploadSymbolsFailed)
      setIsUploading(false)
      setUploadProgress('')
    }
  }

  const renderKeyPreview = () => (
    <div className='tmg-key-preview'>
      <h5 className='tmg-key-preview-title'>{STRINGS.tmgKeyPreview}</h5>
      <div className='tmg-key-preview-row'>
        {Array.from({ length: slotCount }, (_, i) => {
          const digit = i + 1
          const symbol = symbolsByDigit[digit]
          return (
            <div key={digit} className='tmg-key-preview-slot'>
              <div className='tmg-key-preview-image'>
                {symbol?.previewUrl ? (
                  <img src={symbol.previewUrl} alt={`Symbol ${digit}`} />
                ) : (
                  <span className='tmg-placeholder'>—</span>
                )}
              </div>
              <div className='tmg-key-preview-digit'>{digit}</div>
            </div>
          )
        })}
      </div>
      <p className='tmg-helper'>{STRINGS.tmgChangePairingHint}</p>
    </div>
  )

  return (
    <div className='tmg-symbol-key-dialog'>
      <div className='tmg-dialog-header'>
        <h2 className='tmg-dialog-title'>{STRINGS.tmgUploadSymbolKeyTitle}</h2>
        <p className='tmg-dialog-subtext'>
          One ZIP of nine images, each named for the number it maps to:{' '}
          <strong>1.svg … 9.svg</strong>.
        </p>
      </div>

      <hr className='tmg-dialog-divider' />

      <div className='tmg-dialog-body'>
        {!zipFile && (
          <div className='tmg-dialog-upload'>
            <UploadInput
              accept={['.zip']}
              file={{}}
              onUpdateFile={onUpdateFile}
              onRemoveFile={clearPending}
              restrictionText={STRINGS.tmgUploadZipRestriction}
            />
          </div>
        )}

        {zipFile && (
          <>
            <div className='tmg-zip-file-row'>
              <div className='tmg-zip-file-meta'>
                <span className='tmg-zip-file-icon'>{tmgZipFileIcon}</span>
                <div className='tmg-zip-file-text'>
                  <p className='tmg-zip-name'>{zipFile.name}</p>
                  {fileMetaLine && <p className='tmg-zip-meta-line'>{fileMetaLine}</p>}
                </div>
              </div>
              <div className='tmg-zip-actions'>
                <Button
                  link
                  no-margin
                  className='tmg-zip-replace'
                  disabled={isValidating || isUploading}
                  onClick={onReplaceClick}
                  content={
                    <>
                      <span className='tmg-zip-action-icon'>{tmgZipReplaceIcon}</span>
                      Replace
                    </>
                  }
                />
                <Button
                  link
                  no-margin
                  className='tmg-zip-remove'
                  disabled={isValidating || isUploading}
                  onClick={onRemove}
                  content={
                    <>
                      <span className='tmg-zip-action-icon'>{tmgZipRemoveIcon}</span>
                      Remove
                    </>
                  }
                />
              </div>
              <input
                ref={replaceInputRef}
                type='file'
                accept='.zip,application/zip,application/x-zip-compressed'
                className='hidden'
                onChange={onReplaceInputChange}
              />
            </div>

            {error && <p className='tmg-error'>{error}</p>}
            {uploadError && <p className='tmg-error'>{uploadError}</p>}

            {renderKeyPreview()}
          </>
        )}
      </div>

      <hr className='tmg-dialog-divider tmg-dialog-footer-divider' />

      <div className='tmg-dialog-footer'>
        <div className='tmg-dialog-footer-status'>
          {keyComplete && <p className='tmg-key-complete'>{STRINGS.tmgKeyComplete}</p>}
          <p className='tmg-helper tmg-dialog-footer-note'>
            {keyComplete ? (
              STRINGS.tmgUploadDialogFooterNote
            ) : (
              <>
                {STRINGS.tmgKeyIncompleteHint} {STRINGS.tmgPublishedKeyNote}
              </>
            )}
          </p>
        </div>
        <div className='tmg-dialog-actions'>
          <Button grey content='Cancel' onClick={onCancel} disabled={isUploading} />
          <Button
            content={isUploading ? uploadProgress || STRINGS.tmgUploadingSymbols : STRINGS.tmgSaveKey}
            onClick={handleSave}
            disabled={!keyComplete || isUploading}
          />
        </div>
      </div>
    </div>
  )
}

SymbolKeyUploadDialog.propTypes = {
  initialFile: PropTypes.shape({
    name: PropTypes.string,
    size: PropTypes.number,
  }),
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  slotCount: PropTypes.number,
  studyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  uploadImage: PropTypes.func,
}

export default SymbolKeyUploadDialog
